import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Trans, withNamespaces } from 'react-i18next';
import { observer } from 'mobx-react';

import { Link, ExternalLink, routerStore } from 'core/routing';
import { inWhitelabel, getWhitelabelConfig } from 'core/whitelabel';

import {
  MainNavDesktopWrapper,
  NavItem,
  SubnavItem,
  IconStyled,
  IconCaretStyled,
  Subnav,
} from './mainNav.styled';

const websiteLinks = {
  platform: {
    text: 'Platform',
    icon: 'workspace',
    subLinks: {
      'https://wikifactory.com/platform/': {
        text: 'Cloud PDM',
        name: 'https://wikifactory.com/platform/',
      },
      'https://wikifactory.com/cad-rooms/': {
        text: 'CAD Rooms',
        name: 'https://wikifactory.com/cad-rooms/',
      },
      'https://wikifactory.com/marketplace': {
        text: 'Online Manufacturing',
        name: 'https://wikifactory.com/marketplace/',
      },
    },
  },
  // marketplace: {
  //   text: 'Marketplace',
  //   icon: 'marketplace',
  //   subLinks: {
  //     'https://wikifactory.com/marketplace/': {
  //       text: 'Rapid Prototyping',
  //       name: 'https://wikifactory.com/marketplace/',
  //     },
  //     'https://wikifactory-manu.typeform.com/to/d86Nu6Nm': {
  //       // TODO: Create and get Typeform link
  //       text: 'Manufacturing Services',
  //       name: 'https://wikifactory-manu.typeform.com/to/d86Nu6Nm',
  //     },
  //     'https://wikifactory.com/platform/manufacturing-partner/': {
  //       text: 'Become a Manufacturing Partner',
  //       name: 'https://wikifactory.com/platform/manufacturing-partner/',
  //     },
  //   },
  // },
  'https://wikifactory.com/platform/community/': {
    icon: 'community',
    text: 'Community',
    name: 'https://wikifactory.com/platform/community/',
    subLinks: {
      'https://wikifactory.com/community/projects': {
        text: 'Community Projects',
        name: 'https://wikifactory.com/community/projects',
      },
      'https://wikifactory.com/discover/topics': {
        text: 'Topics',
        name: 'https://wikifactory.com/discover/topics',
      },
      'https://wikifactory.com/topic/helpful-resources': {
        text: 'Knowledge Base',
        name: 'https://wikifactory.com/topic/helpful-resources',
      },
    },
  },
  'https://wikifactory.com/pricing': {
    text: 'Plans',
    name: 'https://wikifactory.com/pricing',
    noBlank: true,
  },
};

const MainNav = withNamespaces()(
  observer(props => {
    const { isMobile, isDark } = props;
    const { route } = routerStore;

    const links = Object.entries(websiteLinks);

    const [onHoverList, setOnHoverList] = useState(links.map(() => false));

    const updateOnHoverList = (action, index) => {
      const output = onHoverList.map((bool, listIndex) =>
        index === listIndex ? action : bool,
      );
      if (output !== onHoverList) setOnHoverList(output);
    };

    const defaultMode = () => {
      return links.map(([linkName, linkInfo], index) => {
        const active =
          linkInfo.name === 'home'
            ? linkInfo.name && route.name === linkInfo.name
            : linkInfo.name === 'myFactory'
            ? ['workspace.rfqs', 'workspace.orders'].includes(route.name)
            : linkInfo.name === 'community'
            ? false
            : linkInfo.name && route.name.includes(linkInfo.name);
        const { subLinks } = linkInfo;

        const SubNavSection = subLinks && (
          <Subnav show={onHoverList[index]}>
            {Object.entries(subLinks).map((subItemLink, subIndex) => {
              if (
                subItemLink[0].startsWith('http') ||
                subItemLink[0].startsWith('/')
              ) {
                return (
                  <SubnavItem key={subIndex}>
                    <ExternalLink to={subItemLink[0]} noBlank>
                      <Trans>{subItemLink[1].text}</Trans>
                    </ExternalLink>
                  </SubnavItem>
                );
              }
              if (subItemLink[1].params) {
                return (
                  <SubnavItem
                    key={subItemLink ? `${subItemLink}_${index}` : index}
                  >
                    <Link name={subItemLink[0]} params={subItemLink[1].params}>
                      <Trans>{subItemLink[1].text}</Trans>
                    </Link>
                  </SubnavItem>
                );
              }
              return (
                <SubnavItem
                  key={subItemLink ? `${subItemLink}_${index}` : index}
                >
                  <Link name={subItemLink[0]}>
                    <Trans>{subItemLink[1].text}</Trans>
                  </Link>
                </SubnavItem>
              );
            })}
          </Subnav>
        );

        return (
          <Fragment key={index}>
            <NavItem
              key={linkName + index}
              isActive={!linkInfo.noHighlight && active}
              show={onHoverList[index]}
              onMouseOver={() => {
                if (!isMobile) updateOnHoverList(true, index);
              }}
              onMouseOut={() => {
                if (!isMobile) updateOnHoverList(false, index);
              }}
            >
              {linkInfo.icon && (
                <IconStyled
                  name={linkInfo.icon}
                  size='medium'
                  isDark={isDark}
                  color={active ? '#0f2eff' : '#000000'}
                />
              )}
              {linkInfo?.name?.startsWith('http') ||
              linkInfo?.name?.startsWith('/') ? (
                <ExternalLink
                  to={linkInfo.name}
                  noBlank={linkInfo.name.startsWith('/') || linkInfo.noBlank}
                >
                  <Trans>{linkInfo.text}</Trans>
                </ExternalLink>
              ) : (
                <Link name={linkInfo.name} as={linkInfo.noLink && 'div'}>
                  <Trans>{linkInfo.text}</Trans>
                </Link>
              )}

              {subLinks && !isMobile && (
                <IconCaretStyled
                  name='caret_down'
                  show={onHoverList[index]}
                  isDark={isDark}
                />
              )}
              {subLinks && !isMobile && <>{SubNavSection}</>}
            </NavItem>
            {subLinks && isMobile && <>{SubNavSection}</>}
          </Fragment>
        );
      });
    };

    const whitelabelMode = () => {
      const whitelabelConfig = getWhitelabelConfig();
      return whitelabelConfig.header.map((navItem, index) => {
        // content prop on Link is temp hack to ensure "Projects" link stays intenral link
        return (
          <NavItem key={index} isActive={route.name.includes(navItem.name)}>
            <Link
              name={navItem.name}
              content={{ whitelabel: whitelabelConfig.id }}
            >
              <Trans>{navItem.text}</Trans>
            </Link>
          </NavItem>
        );
      });
    };

    if (isMobile) {
      return <ul>{inWhitelabel() ? whitelabelMode() : defaultMode()}</ul>;
    }

    return (
      <MainNavDesktopWrapper negativeVersion={isDark}>
        {inWhitelabel() ? whitelabelMode() : defaultMode()}
      </MainNavDesktopWrapper>
    );
  }),
);

MainNav.propTypes = {
  isDark: false,
  isMobile: false,
};

MainNav.propTypes = {
  isDark: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export default MainNav;
